import React, { useMemo } from "react";
import { Box, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { ResponsiveLine } from "@nivo/line";
import { chartTheme } from "../../../Reports/Insights/components/Consumption/chartTheme";
import { useGetSuppliersYearlyData } from "../../../../hooks/query";
import LocalSpinner from "../../../../components/LocalSpinner";
import { useToast } from "@chakra-ui/react";
import { constructQueryParamsString, showErrorToast, QUESTIONNAIRE_STATUSES } from "../../../../utils";
import { REPORTING_YEARS } from "../../suppliersMock";

const fmtTco2 = (v) => (typeof v === "number" ? v.toLocaleString("en-GB", { maximumFractionDigits: 0 }) : "—");

const KpiCard = ({ accent, label, value, unit, sublabel }) => (
  <Box position="relative" pt={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" px={4} pb={4} overflow="hidden">
    <Box position="absolute" top={0} left={0} right={0} h="3px" bg={accent} borderRadius="full" />
    <Text fontSize="11px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" mt={1}>
      {label}
    </Text>
    <Flex align="baseline" gap={2} mt={2}>
      <Text fontSize="28px" fontWeight="700" color="darkTeal" lineHeight="1">{value}</Text>
      {unit && <Text fontSize="13px" color="gray.500" fontWeight="600">{unit}</Text>}
    </Flex>
    {sublabel && <Text fontSize="11px" color="gray.500" mt={1}>{sublabel}</Text>}
  </Box>
);

// Same years range as the working Insights supplier trend chart (2021 → latest reporting year)
const CHART_YEARS = Array.from(
  { length: REPORTING_YEARS[REPORTING_YEARS.length - 1] - 2021 + 1 },
  (_, i) => 2021 + i
);

function SupplierDashboard({ supplier }) {
  const toast = useToast();

  // Same endpoint the Insights supplier dashboard uses — GET /suppliers/reports/yearly-emission.
  // Filtered by supplier NAME (available immediately from router state) rather than the UUID —
  // the UUID is only resolved asynchronously by the parent and waiting on it serialises this
  // fetch behind that one, making the dashboard load visibly later than the rest of the page.
  // Response: [{ supplierId, yearlyEmissionData: { [year]: { totalCo2Emissions, ... } } }]
  const yearlyQueryString = useMemo(
    () => constructQueryParamsString({ years: CHART_YEARS, supplier: supplier.name, showExcluded: true, showSupplierWithoutData: true }),
    [supplier.name]
  );

  const { data: yearlyData, isFetching } = useGetSuppliersYearlyData(yearlyQueryString, {
    enabled: !!supplier.name,
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  const emissionsPerYear = useMemo(() => {
    // Name filter is a substring (ilike) match on the backend — pick the exact match.
    const match = yearlyData?.find((row) => row.supplierName === supplier.name) ?? yearlyData?.[0];
    const yearlyEmissionData = match?.yearlyEmissionData;
    if (!yearlyEmissionData) return null;
    return Object.entries(yearlyEmissionData).map(([year, values]) => ({
      year: Number(year),
      totalCo2Emissions: values?.totalCo2Emissions ?? 0,
    }));
  }, [yearlyData, supplier.name]);

  // Derive task counts from engagement data passed in supplier object
  const taskStats = useMemo(() => {
    if (!supplier.engagementData) return { total: 0, outstanding: 0 };
    const allQuestionnaires = Object.values(supplier.engagementData).flat();
    const total = allQuestionnaires.length;
    const outstanding = allQuestionnaires.filter(
      (q) => q.status !== QUESTIONNAIRE_STATUSES.FINALIZED
    ).length;
    return { total, outstanding };
  }, [supplier.engagementData]);

  // Chart: array sorted by year, x = year string, y = totalCo2Emissions
  const chartData = useMemo(() => {
    if (!emissionsPerYear || emissionsPerYear.length === 0) return null;
    const points = [...emissionsPerYear]
      .sort((a, b) => a.year - b.year)
      .map((el) => ({ x: String(el.year), y: el.totalCo2Emissions ?? 0 }));
    return [{ id: "Attributed emissions", color: "#85C22A", data: points }];
  }, [emissionsPerYear]);

  // KPI cards: last 3 REPORTING_YEARS
  const lastThree = useMemo(() => {
    if (!emissionsPerYear) return REPORTING_YEARS.slice(-3).map((y) => ({ year: y, tco2e: null }));
    return REPORTING_YEARS.slice(-3).map((y) => {
      const el = emissionsPerYear.find((e) => String(e.year) === String(y));
      return { year: y, tco2e: el?.totalCo2Emissions ?? null };
    });
  }, [emissionsPerYear]);

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        <KpiCard
          accent="#FBBF24"
          label="Outstanding tasks"
          value={taskStats.outstanding}
          sublabel={`of ${taskStats.total} data tasks`}
        />
        {isFetching ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Box key={i} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} display="flex" alignItems="center" justifyContent="center">
              <LocalSpinner />
            </Box>
          ))
        ) : lastThree.map((e, idx) => (
          <KpiCard
            key={e.year}
            accent={idx === lastThree.length - 1 ? "lime" : "pureBlack"}
            label={`${e.year} emissions`}
            value={e.tco2e !== null ? fmtTco2(e.tco2e) : "—"}
            unit={e.tco2e !== null ? "tCO₂e" : undefined}
            sublabel="Supplier attributed"
          />
        ))}
      </SimpleGrid>

      <Box>
        <Text fontSize="body.m" fontWeight="600" color="gray.700">Emissions over time</Text>
        <Text fontSize="body.xs" color="gray.500" mb={3}>
          Supplier-attributed tCO₂e across all available reporting years.
        </Text>
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h="300px">
          {isFetching ? (
            <Flex h="100%" align="center" justify="center"><LocalSpinner /></Flex>
          ) : chartData ? (
            <ResponsiveLine
              data={chartData}
              margin={{ top: 12, right: 24, bottom: 36, left: 64 }}
              xScale={{ type: "point" }}
              yScale={{ type: "linear", min: 0, max: "auto", stacked: false }}
              colors={({ color }) => color}
              curve="monotoneX"
              enableArea
              areaOpacity={0.12}
              enablePoints
              pointSize={6}
              pointBorderWidth={2}
              pointBorderColor={{ from: "serieColor" }}
              pointColor="#FFFFFF"
              axisBottom={{ tickSize: 0, tickPadding: 6 }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 6,
                format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
              }}
              enableGridX={false}
              theme={chartTheme}
              tooltip={({ point }) => (
                <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
                  <Text fontWeight="700" color="gray.800">{point.data.x}</Text>
                  <Text color={point.serieColor}>{fmtTco2(point.data.y)} tCO₂e</Text>
                </Box>
              )}
              useMesh
            />
          ) : (
            <Flex h="100%" align="center" justify="center">
              <Text fontSize="14px" color="gray.400">No emissions data available.</Text>
            </Flex>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default SupplierDashboard;
